import { useEffect, useState } from 'react';
import { PlayerType } from '../../../../models/video';
import { Button } from '../../../atoms/Button';
import Player from '../../../molecules/Player';
import PlayerAprova from '../../../atoms/PlayerAprova';
import PlayerLiveChat from '../../../molecules/PlayerLiveChat';
import { PlayerQuiz } from '../../PlayerQuiz';

type ResolvedPlayer = 'aprova' | 'legacy';

interface LessonPlayerProps {
  id: string;
  discipline: string;
  title: string;
  token: string;
  playerType: PlayerType;
  seekTo?: { seconds: number; requestId: number };
  onFinish?: (origin?: 'virtual' | 'ended') => any;
  virtualFinishAt?: number;
  onVideoEnd?: () => void;
  /** Segundos assistidos, vindo de qualquer um dos players. */
  onProgressSeconds?: (seconds: number) => void;
  isLiveStream: boolean;
  showLiveChat: boolean;
  quiz: any;
  isQuizOpen: boolean;
  setIsQuizOpen: (open: boolean) => void;
  showQuizFeature: boolean;
  embedUrl?: string;
  defaultPlayer?: PlayerType;
  captionUri?: string;
}

export default function LessonPlayer({
  id,
  discipline,
  title,
  token,
  playerType,
  seekTo,
  onFinish,
  virtualFinishAt,
  onVideoEnd,
  onProgressSeconds,
  isLiveStream,
  showLiveChat,
  quiz,
  isQuizOpen,
  setIsQuizOpen,
  showQuizFeature,
  captionUri,
  embedUrl,
}: LessonPlayerProps) {
  // Resolve o player uma única vez: prioriza o novo player (embedUrl); na falta
  // dele, usa o player antigo (vimeo/sambatech). Mantemos `null` até a validação
  // terminar para não trocar o player em tela (pop-in) para o usuário.
  const [resolvedPlayer, setResolvedPlayer] = useState<ResolvedPlayer | null>(
    null
  );

  useEffect(() => {
    setResolvedPlayer(embedUrl ? 'aprova' : 'legacy');
  }, [embedUrl]);

  const hasQuizOverlay =
    quiz && quiz.exercises.length > 0 && !isQuizOpen && showQuizFeature;

  const showQuiz = quiz && isQuizOpen && showQuizFeature;

  function renderPlayer() {
    if (showQuiz) {
      return (
        <PlayerQuiz quiz={quiz} isOpen={isQuizOpen} setIsOpen={setIsQuizOpen} />
      );
    }

    if (resolvedPlayer === null) {
      return (
        <div
          className='h-[465px] w-full animate-pulse bg-black'
          aria-busy
          aria-label='Carregando player'
        />
      );
    }

    // O player novo recebe o mesmo contrato do legado: sem onFinish a aula
    // não era marcada como vista nem pontuava no Game.
    if (resolvedPlayer === 'aprova') {
      return (
        <>
          {/* Âncora do widget da Kira: o script externo procura este
              [data-content-id] no DOM. O player legado a renderiza pelo
              molecule <Player>; aqui replicamos para o player novo (AWS).
              Só quando há transcrição (captionUri), como nas demais (DEV-12118). */}
          {/*{captionUri ? <div data-content-id={id} /> : null}*/}
          <PlayerAprova
            embedUrl={embedUrl}
            title={title}
            className='w-full'
            onProgressSeconds={onProgressSeconds}
            onFinish={onFinish}
            virtualFinishAt={virtualFinishAt}
            onVideoEnd={onVideoEnd}
            seekTo={seekTo}
          />
        </>
      );
    }

    return (
      <Player
        contentId={id}
        onFinish={onFinish}
        virtualFinishAt={virtualFinishAt}
        onVideoEnd={onVideoEnd}
        onProgressSeconds={onProgressSeconds}
        token={token}
        playerType={playerType}
        initialTime={0}
        seekTo={seekTo}
        className='h-[465px]'
        captionUri={captionUri}
      />
    );
  }

  return (
    <div>
      <div
        className='relative flex flex-col lg:flex-row'
        id='lesson-player-container'
        data-discipline={discipline}
        data-id={id}
        data-title={title}
      >
        {hasQuizOverlay && (
          <Button
            variant='contained'
            LeftIcon='quiz'
            onClick={() => setIsQuizOpen(true)}
            negative
            className='group absolute right-5 top-5 z-[3]'
          >
            <span className='hidden group-hover:block'>
              Responda ao quiz da aula
            </span>
          </Button>
        )}

        {renderPlayer()}

        {isLiveStream && showLiveChat && (
          <PlayerLiveChat token={token} playerType={playerType} />
        )}
      </div>
    </div>
  );
}
